import React, { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Bell, CheckCircle, XCircle, Info, Trash, ArrowClockwise } from "@phosphor-icons/react";

const ICONS = {
    slip_verified: CheckCircle,
    slip_rejected: XCircle,
    slip_updated: Info,
};
const ACCENT = {
    slip_verified: "text-emerald-600 bg-emerald-50",
    slip_rejected: "text-rose-600 bg-rose-50",
    slip_updated: "text-violet-600 bg-violet-50",
};
const formatWhen = (iso) => {
    if (!iso) return "—";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "—";
    const p = (n) => String(n).padStart(2, "0");
    return `${p(d.getDate())}/${p(d.getMonth() + 1)}/${d.getFullYear()} ${p(d.getHours())}:${p(d.getMinutes())}`;
};

export default function CustomerNotifications() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/notifications", { params: { limit: 200 } });
            setItems(Array.isArray(data) ? data : []);
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setLoading(false); }
    }, []);

    useEffect(() => {
        load();
        const t = setInterval(load, 20000);
        return () => clearInterval(t);
    }, [load]);

    const markAllRead = async () => {
        try {
            await api.post("/notifications/mark-read", {});
            setItems((prev) => prev.map((n) => ({ ...n, read: true })));
            toast.success("Marked all as read");
        } catch (e) { toast.error(formatApiError(e)); }
    };

    const clearAll = async () => {
        if (!window.confirm("Delete all notifications?")) return;
        try {
            const { data } = await api.delete("/notifications/clear");
            setItems([]);
            toast.success(`Cleared ${data.deleted ?? 0} notifications`);
        } catch (e) { toast.error(formatApiError(e)); }
    };

    const unreadCount = items.filter((n) => !n.read).length;

    return (
        <div data-testid="customer-notifications-page" className="space-y-5">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
                <div>
                    <h1 className="font-display text-2xl font-bold text-slate-900">Notifications</h1>
                    <p className="mt-1 text-sm text-slate-500">Updates from MICM Admin about your slips.</p>
                </div>
                <div className="flex flex-wrap gap-2">
                    <Button variant="outline" size="sm" onClick={load}><ArrowClockwise size={14} className="mr-1.5" /> Refresh</Button>
                    <Button variant="outline" size="sm" onClick={markAllRead} disabled={unreadCount === 0}><CheckCircle size={14} className="mr-1.5" /> Mark all read</Button>
                    <Button variant="outline" size="sm" onClick={clearAll} disabled={items.length === 0} className="text-rose-600 hover:bg-rose-50"><Trash size={14} className="mr-1.5" /> Clear</Button>
                </div>
            </div>
            <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
                {loading && items.length === 0 && <div className="px-6 py-16 text-center text-sm text-slate-400">Loading…</div>}
                {!loading && items.length === 0 && (
                    <div className="flex flex-col items-center gap-2 px-6 py-16 text-center text-slate-400">
                        <Bell size={36} /><div className="text-sm">No notifications yet.</div>
                    </div>
                )}
                <ul>
                    {items.map((n) => {
                        const Icon = ICONS[n.type] || Bell;
                        const accent = ACCENT[n.type] || "text-slate-500 bg-slate-100";
                        return (
                            <li key={n.id} className={`flex items-start gap-3 border-b border-slate-100 px-5 py-4 last:border-0 ${n.read ? "" : "bg-blue-50/40"}`}>
                                <div className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full ${accent}`}>
                                    <Icon size={16} weight="bold" />
                                </div>
                                <div className="min-w-0 flex-1">
                                    <div className="flex items-center gap-2">
                                        <div className={`text-sm font-semibold ${n.read ? "text-slate-700" : "text-slate-900"}`}>{n.title}</div>
                                        {!n.read && <span className="h-1.5 w-1.5 rounded-full bg-blue-500" />}
                                    </div>
                                    {n.body && <div className="mt-1 text-xs text-slate-600">{n.body}</div>}
                                    <div className="mt-1.5 text-[11px] text-slate-400">{formatWhen(n.created_at)}</div>
                                </div>
                            </li>
                        );
                    })}
                </ul>
            </div>
        </div>
    );
}
